/**
 * Grant Grid Service
 * Handles grid state management for the visual permission matrix
 *
 * Grid Structure:
 * - Rows: ClientResourceMap entries (enabled resources for the client)
 * - Columns: Action enums (VIEW, EDIT, CREATE, DELETE, DEACTIVATE, EXPORT)
 * - Cells: Grant state (ALLOW, DENY, NONE, CONFLICT)
 */

import { API, graphqlOperation } from 'aws-amplify';
import { clientResourceMapByClient, getResourceCatalog } from 'graphql/queries';
import { listGrants } from 'graphql/queries';
import { createGrant, updateGrant, deleteGrant } from 'graphql/mutations';
import logger from 'utils/logger';

/**
 * Action enums supported by the permission system
 */
export const ACTIONS = ['VIEW', 'EDIT', 'CREATE', 'DELETE', 'DEACTIVATE', 'EXPORT'];

/**
 * Cell states for the grid
 */
export const CELL_STATES = {
  NONE: 'NONE',       // Gray - No grant exists
  ALLOW: 'ALLOW',     // Green - ALLOW grant exists
  DENY: 'DENY',       // Red - DENY grant exists
  CONFLICT: 'CONFLICT' // Black - Both ALLOW and DENY grants exist (error state)
};

/**
 * Load grid data for a specific client and subject criteria (v5 schema)
 * @param {string} clientId - Client ID
 * @param {Object} subjectCriteria - Subject selection criteria
 * @param {string} subjectCriteria.subjectType - Subject type (ROLE, USER)
 * @param {string} subjectCriteria.subjectValue - Role name (MANAGER, ASSOCIATE, etc.) or userId
 * @returns {Promise<Object>} Grid data with resources and grants
 */
export async function loadGridData(clientId, subjectCriteria) {
  try {
    logger.info('Loading grid data', { clientId, subjectCriteria });

    // Validate subject criteria
    if (!subjectCriteria.subjectValue) {
      logger.warn('Subject criteria incomplete - subjectValue is null', { subjectCriteria });
      // Return empty grid state when subject criteria is incomplete
      return {
        resources: [],
        grants: [],
        gridState: new Map(),
        subjectCriteria
      };
    }

    // Load enabled resources for the client
    const resourcesResponse = await API.graphql(
      graphqlOperation(clientResourceMapByClient, {
        clientId,
        filter: { isEnabled: { eq: true } },
        sortDirection: 'ASC'
      })
    );

    const resourceMaps = resourcesResponse.data.clientResourceMapByClient.items || [];
    logger.info('Loaded resource maps', { count: resourceMaps.length });

    // Enhance resources with ResourceCatalog details (domain, resourceTypeId)
    const resources = await Promise.all(
      resourceMaps.map(async (resourceMap) => {
        try {
          const catalogResponse = await API.graphql(
            graphqlOperation(getResourceCatalog, { id: resourceMap.resourceId })
          );
          const catalog = catalogResponse.data.getResourceCatalog;
          return {
            ...resourceMap,
            // Add catalog details for structured targeting
            domain: catalog?.domain,
            resourceTypeId: catalog?.resourceTypeId,
            resourceCode: catalog?.resourceCode,
            catalogName: catalog?.name,
            defaultActions: catalog?.defaultActions || []
          };
        } catch (error) {
          logger.warn('Failed to fetch catalog for resource', { resourceId: resourceMap.resourceId, error });
          return {
            ...resourceMap,
            domain: null,
            resourceTypeId: null,
            resourceCode: resourceMap.resourceId,
            catalogName: resourceMap.customLabel || resourceMap.resourceId,
            defaultActions: []
          };
        }
      })
    );

    logger.info('Enhanced resources with catalog details', { count: resources.length });

    // Load existing grants for the client and subject criteria
    const grantsFilter = buildGrantsFilter(clientId, subjectCriteria);

    let grantsResponse;
    try {
      grantsResponse = await API.graphql(
        graphqlOperation(listGrants, { filter: grantsFilter })
      );
    } catch (graphqlError) {
      // API.graphql may throw when there are errors, but the error object contains partial data
      // Check if the error object has data we can use
      if (graphqlError.data && graphqlError.data.listGrants) {
        logger.warn('GraphQL threw error but returned partial data (likely legacy grants with null actionConfigs)', {
          errorCount: graphqlError.errors?.length || 0,
          errors: graphqlError.errors?.map(e => ({ message: e.message, path: e.path })) || []
        });
        grantsResponse = graphqlError; // Use the error object as the response
      } else {
        // Real error without usable data
        logger.error('GraphQL error without usable data', { graphqlError });
        throw new Error(`Failed to load grants: ${graphqlError.errors?.[0]?.message || graphqlError.message || 'Unknown error'}`);
      }
    }

    // Handle GraphQL errors from legacy grants with null actionConfigs
    if (grantsResponse.errors && grantsResponse.errors.length > 0) {
      logger.warn('GraphQL returned errors (likely legacy grants with null actionConfigs)', {
        errorCount: grantsResponse.errors.length,
        errors: grantsResponse.errors.map(e => ({ message: e.message, path: e.path }))
      });
    }

    // Safely extract items from response
    if (!grantsResponse.data || !grantsResponse.data.listGrants) {
      logger.error('GraphQL response missing expected data structure', { grantsResponse });
      throw new Error('Failed to load grants: Invalid response structure');
    }

    // Filter out null items (grants that failed to load due to null actionConfigs)
    const allItems = grantsResponse.data.listGrants.items || [];
    const grants = allItems.filter(item => item !== null);
    const nullCount = allItems.length - grants.length;

    if (nullCount > 0) {
      logger.warn('Filtered out legacy grants with null actionConfigs', {
        nullCount,
        validGrantCount: grants.length,
        totalAttempted: allItems.length
      });
    }

    logger.info('Loaded grants', { count: grants.length });

    // Build grid state matrix
    const gridState = buildGridState(resources, grants);

    return {
      resources,
      grants,
      gridState,
      subjectCriteria
    };
  } catch (error) {
    logger.error('Failed to load grid data', { error, clientId, subjectCriteria });

    // Extract meaningful error message from various error formats
    let errorMessage = 'Unknown error';
    if (error.message) {
      errorMessage = error.message;
    } else if (error.errors && error.errors.length > 0) {
      errorMessage = error.errors[0].message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    throw new Error(`Unable to load grid data: ${errorMessage}`);
  }
}

/**
 * Build filter for grants query based on subject criteria (v5 schema)
 * @param {string} clientId - Client ID
 * @param {Object} subjectCriteria - Subject selection criteria
 * @param {string} subjectCriteria.subjectType - "ROLE" | "USER"
 * @param {string} subjectCriteria.subjectValue - Role name (MANAGER, etc.) or userId
 * @returns {Object} GraphQL filter object
 */
function buildGrantsFilter(clientId, subjectCriteria) {
  const filter = {
    subjectType: { eq: subjectCriteria.subjectType }
  };

  // APP_ADMIN is a cross-client role - don't filter by clientId for APP_ADMIN
  // This allows APP_ADMIN to see their grants regardless of which client is selected
  // For all other roles/users, filter by the selected client
  if (subjectCriteria.subjectValue !== 'APP_ADMIN') {
    filter.clientId = { eq: clientId };
  }

  // v5 schema: Use subjectValue for both ROLE and USER grants
  if (subjectCriteria.subjectValue) {
    filter.subjectValue = { eq: subjectCriteria.subjectValue };
  }

  return filter;
}

/**
 * Build grid state matrix from resources and grants
 * Handles both v4 pattern-based and v5 structured grants
 * @param {Array} resources - ClientResourceMap items with catalog details
 * @param {Array} grants - Grant items
 * @returns {Map<string, Map<string, Object>>} Grid state keyed by resourceId and action
 */
function buildGridState(resources, grants) {
  const gridState = new Map();

  // Initialize grid with NONE state for all cells
  resources.forEach(resource => {
    const resourceRow = new Map();
    ACTIONS.forEach(action => {
      resourceRow.set(action, {
        state: CELL_STATES.NONE,
        grants: []
      });
    });
    gridState.set(resource.resourceId, resourceRow);
  });

  // Populate grants into grid
  grants.forEach(grant => {
    // v5.002: Use actionConfigs array
    if (grant.actionConfigs && Array.isArray(grant.actionConfigs)) {
      // Process each action configuration
      grant.actionConfigs.forEach(actionConfig => {
        const { action, effect, scopeConfig } = actionConfig;

        if (!action) {
          logger.warn('ActionConfig missing action field', { grantId: grant.id, actionConfig });
          return;
        }

        // Determine which resources this grant applies to
        const applicableResources = [];

        // Check if targetResourceId is a wildcard (null, undefined, or "*")
        const isResourceIdWildcard = !grant.targetResourceId || grant.targetResourceId === '*';

        if (!isResourceIdWildcard) {
          // Specific resource grant - only applies to one resource
          if (gridState.has(grant.targetResourceId)) {
            applicableResources.push(grant.targetResourceId);
          }
        } else {
          // Wildcard grant - applies to all resources that match domain/resourceType filters
          resources.forEach(resource => {
            // Check if grant's domain filter matches (null, undefined, or "*" = wildcard = matches all)
            const domainMatches = !grant.targetDomain || grant.targetDomain === '*' || grant.targetDomain === resource.domain;

            // Check if grant's resourceType filter matches (null, undefined, or "*" = wildcard = matches all)
            const typeMatches = !grant.targetResourceTypeId || grant.targetResourceTypeId === '*' || grant.targetResourceTypeId === resource.resourceTypeId;

            if (domainMatches && typeMatches) {
              applicableResources.push(resource.resourceId);
            }
          });
        }

        // Apply grant to all applicable resources
        applicableResources.forEach(resourceId => {
          const resourceRow = gridState.get(resourceId);
          if (resourceRow && resourceRow.has(action)) {
            const cell = resourceRow.get(action);

            // Store grant reference with action-specific effect
            cell.grants.push({
              ...grant,
              effect, // effect from actionConfig
              scopeConfig // scopeConfig from actionConfig
            });

            // Determine cell state based on all grants for this cell
            const hasAllow = cell.grants.some(g => g.effect === 'ALLOW');
            const hasDeny = cell.grants.some(g => g.effect === 'DENY');

            if (hasAllow && hasDeny) {
              cell.state = CELL_STATES.CONFLICT;
            } else if (hasAllow) {
              cell.state = CELL_STATES.ALLOW;
            } else if (hasDeny) {
              cell.state = CELL_STATES.DENY;
            }
          }
        });
      });
    } else {
      // Legacy grants without actionConfigs - skip them
      // They've already been filtered out, but just in case
      logger.warn('Grant missing actionConfigs array (legacy grant)', {
        grantId: grant.id,
        name: grant.name
      });
    }
  });

  return gridState;
}

/**
 * Parse resource pattern to extract resourceId and action
 * Format: [domain]:[resourceType]:[resourceId]:[action]
 * @param {string} resourcePattern - Resource pattern string
 * @returns {Object} Parsed components
 */
function parseResourcePattern(resourcePattern) {
  const parts = resourcePattern.split(':');
  return {
    domain: parts[0] || '*',
    resourceType: parts[1] || '*',
    resourceId: parts[2] || '*',
    action: parts[3] || '*'
  };
}

/**
 * Build resource pattern from components
 * @param {string} domain - Domain
 * @param {string} resourceType - Resource type
 * @param {string} resourceId - Resource ID (resourceCode)
 * @param {string} action - Action
 * @returns {string} Resource pattern
 */
function buildResourcePattern(domain, resourceType, resourceId, action) {
  return `${domain}:${resourceType}:${resourceId}:${action}`;
}

/**
 * Toggle cell state in the grid
 * @param {Object} currentCell - Current cell state
 * @returns {string} Next cell state
 */
export function toggleCellState(currentCell) {
  const { state } = currentCell;

  switch (state) {
    case CELL_STATES.NONE:
      return CELL_STATES.ALLOW;
    case CELL_STATES.ALLOW:
      return CELL_STATES.DENY;
    case CELL_STATES.DENY:
      return CELL_STATES.NONE;
    case CELL_STATES.CONFLICT:
      // On conflict, clear all grants and set to NONE
      return CELL_STATES.NONE;
    default:
      return CELL_STATES.NONE;
  }
}

/**
 * Save grid changes by creating/updating/deleting grants
 * @param {string} clientId - Client ID
 * @param {Object} subjectCriteria - Subject criteria
 * @param {Map} gridState - Current grid state
 * @param {Map} originalGridState - Original grid state for comparison
 * @param {Object} scopeSettings - Scope settings per resource
 * @returns {Promise<Object>} Save result with counts
 */
export async function saveGridChanges(clientId, subjectCriteria, gridState, originalGridState, scopeSettings, resources) {
  try {
    logger.info('Saving grid changes', { clientId, subjectCriteria });

    const changes = {
      toCreate: [],
      toUpdate: [],
      toDelete: []
    };

    // Build resource map for quick lookup
    const resourceMap = new Map(resources.map(r => [r.resourceId, r]));

    // Compare current grid state with original to determine changes
    for (const [resourceId, resourceRow] of gridState) {
      const originalRow = originalGridState.get(resourceId);
      const resource = resourceMap.get(resourceId);

      for (const [action, cell] of resourceRow) {
        const originalCell = originalRow?.get(action);
        const hasChanged = !originalCell || cell.state !== originalCell.state;

        if (hasChanged) {
          const change = buildGrantChange(
            clientId,
            resourceId,
            action,
            cell,
            originalCell,
            subjectCriteria,
            scopeSettings.get(resourceId) || {},
            resource
          );

          if (change.operation === 'CREATE') {
            changes.toCreate.push(change.data);
          } else if (change.operation === 'UPDATE') {
            changes.toUpdate.push(change.data);
          } else if (change.operation === 'DELETE') {
            changes.toDelete.push(change.data);
          }
        }
      }
    }

    logger.info('Changes to apply', {
      create: changes.toCreate.length,
      update: changes.toUpdate.length,
      delete: changes.toDelete.length
    });

    // Execute changes
    const results = await executeGrantChanges(changes);

    logger.info('Grid changes saved successfully', results);
    return results;
  } catch (error) {
    logger.error('Failed to save grid changes', { error });
    throw new Error(`Unable to save changes: ${error.message}`);
  }
}

/**
 * Build grant change object based on cell state transition
 * @param {string} clientId - Client ID
 * @param {string} resourceId - Resource ID (ResourceCatalog ID)
 * @param {string} action - Action
 * @param {Object} newCell - New cell state
 * @param {Object} oldCell - Old cell state
 * @param {Object} subjectCriteria - Subject criteria
 * @param {Object} scopeSettings - Scope settings for this resource
 * @param {Object} resource - Resource object with catalog details
 * @returns {Object} Change operation
 */
function buildGrantChange(clientId, resourceId, action, newCell, oldCell, subjectCriteria, scopeSettings, resource) {
  // Map subjectCriteria to Grant model fields
  const grantFields = {
    clientId,
    // v5 structured targeting fields
    targetDomain: resource?.domain || null, // null = wildcard
    targetResourceTypeId: resource?.resourceTypeId || null, // null = wildcard
    targetResourceId: resourceId, // specific resource ID from ResourceCatalog
    action: action, // specific action
    // v4 legacy fields (maintained for backward compatibility)
    subjectType: subjectCriteria.subjectType,
    isActive: true,
    priority: 100 // Default priority
  };

  // Set subject-specific fields
  if (subjectCriteria.subjectType === 'USER') {
    grantFields.subjectValue = subjectCriteria.userId;
    // For USER grants, still need targetGroup, targetGroupRole, targetOrgLevel
    grantFields.targetGroup = 'OPERATIONS'; // Default
    grantFields.targetGroupRole = 'ASSOCIATE'; // Default
    grantFields.targetOrgLevel = 'LOCATION_LEVEL'; // Default
  } else {
    // For ROLE grants
    grantFields.subjectValue = subjectCriteria.groupRole; // Store the role name in subjectValue
    grantFields.targetGroup = subjectCriteria.domain;
    grantFields.targetGroupRole = subjectCriteria.groupRole;
    grantFields.targetOrgLevel = subjectCriteria.orgLevel;
  }

  // Build scope config
  const scopeConfig = {
    actions: {
      [action]: { permission: 'ALLOW' }
    },
    includeChildren: scopeSettings.includeChildren || false,
    includePeers: scopeSettings.includePeers || false,
    includeParent: scopeSettings.includeParent || false
  };

  const baseGrant = {
    ...grantFields,
    scopeConfig: JSON.stringify(scopeConfig)
  };

  // Determine operation based on state transition
  if (newCell.state === CELL_STATES.NONE) {
    // Delete existing grants
    if (oldCell && oldCell.grants.length > 0) {
      return {
        operation: 'DELETE',
        data: oldCell.grants.map(g => g.id)
      };
    }
    return { operation: 'NONE' };
  }

  if (newCell.state === CELL_STATES.ALLOW || newCell.state === CELL_STATES.DENY) {
    const effect = newCell.state === CELL_STATES.ALLOW ? 'ALLOW' : 'DENY';

    if (oldCell && oldCell.grants.length > 0) {
      // Update existing grant
      return {
        operation: 'UPDATE',
        data: {
          id: oldCell.grants[0].id,
          effect,
          scopeConfig: baseGrant.scopeConfig
        }
      };
    }

    // Create new grant
    const resourceName = resource?.catalogName || resource?.customLabel || resource?.resourceCode || resourceId;
    return {
      operation: 'CREATE',
      data: {
        ...baseGrant,
        effect,
        name: `${resourceName} - ${action}`,
        description: `Grant Admin grid: ${effect} ${action} on ${resourceName} for ${subjectCriteria.subjectType === 'USER' ? subjectCriteria.userId : subjectCriteria.groupRole}`
      }
    };
  }

  return { operation: 'NONE' };
}

/**
 * Build data scope from scope settings
 * @param {Object} scopeSettings - Scope checkboxes
 * @returns {Object} DataScope object
 */
function buildDataScope(scopeSettings) {
  return {
    scopeType: 'HIERARCHICAL',
    includeChildren: scopeSettings.includeChildren || false,
    includePeers: scopeSettings.includePeers || false,
    includeParent: scopeSettings.includeParent || false
  };
}

/**
 * Execute grant changes (create, update, delete)
 * @param {Object} changes - Changes to execute
 * @returns {Promise<Object>} Execution results
 */
async function executeGrantChanges(changes) {
  const results = {
    created: 0,
    updated: 0,
    deleted: 0,
    errors: []
  };

  // Create grants
  for (const grantData of changes.toCreate) {
    try {
      await API.graphql(graphqlOperation(createGrant, { input: grantData }));
      results.created++;
    } catch (error) {
      logger.error('Failed to create grant', { error, grantData });
      results.errors.push({ operation: 'CREATE', error: error.message, data: grantData });
    }
  }

  // Update grants
  for (const grantData of changes.toUpdate) {
    try {
      await API.graphql(graphqlOperation(updateGrant, { input: grantData }));
      results.updated++;
    } catch (error) {
      logger.error('Failed to update grant', { error, grantData });
      results.errors.push({ operation: 'UPDATE', error: error.message, data: grantData });
    }
  }

  // Delete grants
  for (const grantIds of changes.toDelete) {
    for (const grantId of grantIds) {
      try {
        await API.graphql(graphqlOperation(deleteGrant, { input: { id: grantId } }));
        results.deleted++;
      } catch (error) {
        logger.error('Failed to delete grant', { error, grantId });
        results.errors.push({ operation: 'DELETE', error: error.message, data: grantId });
      }
    }
  }

  return results;
}

/**
 * Apply bulk action to selected cells
 * @param {Map} gridState - Grid state
 * @param {Array} selectedCells - Selected cell coordinates [{resourceId, action}]
 * @param {string} bulkAction - Action to apply (ALLOW, DENY, CLEAR)
 * @returns {Map} Updated grid state
 */
export function applyBulkAction(gridState, selectedCells, bulkAction) {
  const newGridState = new Map(gridState);

  selectedCells.forEach(({ resourceId, action }) => {
    if (newGridState.has(resourceId)) {
      const resourceRow = newGridState.get(resourceId);
      if (resourceRow.has(action)) {
        const cell = resourceRow.get(action);

        if (bulkAction === 'CLEAR') {
          cell.state = CELL_STATES.NONE;
        } else if (bulkAction === 'ALLOW') {
          cell.state = CELL_STATES.ALLOW;
        } else if (bulkAction === 'DENY') {
          cell.state = CELL_STATES.DENY;
        }
      }
    }
  });

  return newGridState;
}

/**
 * Trigger permission recalculation for affected users
 * @param {string} clientId - Client ID
 * @param {Object} subjectCriteria - Subject criteria
 * @returns {Promise<void>}
 */
export async function triggerPermissionRecalculation(clientId, subjectCriteria) {
  try {
    // TODO: Implement call to permRecalculateUserPermissions Lambda
    logger.info('Triggering permission recalculation', { clientId, subjectCriteria });

    // For now, log that this needs to be implemented
    logger.warn('Permission recalculation not yet implemented');
  } catch (error) {
    logger.error('Failed to trigger permission recalculation', { error });
    throw new Error(`Unable to trigger recalculation: ${error.message}`);
  }
}
